import { CdkScrollable } from "@angular/cdk/scrolling";
import { Component, OnDestroy, OnInit, ViewChild, inject } from "@angular/core";
import { AbstractControl, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { MatButton, MatIconButton } from "@angular/material/button";
import { MatCard, MatCardContent } from "@angular/material/card";
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogClose, MatDialogContent, MatDialogRef, MatDialogTitle } from "@angular/material/dialog";
import { MatDivider } from "@angular/material/divider";
import { MatIcon } from "@angular/material/icon";
import { MatMenu, MatMenuItem, MatMenuTrigger } from "@angular/material/menu";
import { MatProgressBar } from "@angular/material/progress-bar";
import { FlexDirective, LayoutAlignDirective, LayoutDirective } from "@ngbracket/ngx-layout/flex";
import { LgdFormControlFactory, LgdSelectComponent, LgdTextComponent, LgdTextFormControl, setEnabled } from "@tylertech/lgd-reactive-forms";
import { KeyValue } from "@tylertech/tyler-api-types";
import { BehaviorSubject, Observable, Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { NewPacketTemplateDTO } from "../../interfaces";
import { Dictionary, UBDashboardService } from "../../services";

export interface NewPacketTemplateDialogData
{
    readonly Item: NewPacketTemplateDTO;
}

function dictionaryToList(dict: Dictionary<string>): ReadonlyArray<KeyValue>
{
    return Object.entries(dict)
        .map(([key, value]: [string, string]) =>
        {
            return {
                Key: key,
                Value: value
            };
        });
}

@Component({
    selector: "new-packet-template-dialog-component",
    templateUrl: "./newPacketTemplateDialog.tmpl.html",
    imports: [
        CdkScrollable,
        FlexDirective,
        LayoutAlignDirective,
        LayoutDirective,
        LgdSelectComponent,
        LgdTextComponent,
        MatButton,
        MatCard,
        MatCardContent,
        MatDialogActions,
        MatDialogClose,
        MatDialogContent,
        MatDialogTitle,
        MatDivider,
        MatIcon,
        MatIconButton,
        MatMenu,
        MatMenuItem,
        MatMenuTrigger,
        MatProgressBar,
        ReactiveFormsModule,
    ]
})
export class NewPacketTemplateDialogComponent implements OnDestroy, OnInit
{
    private readonly dialogRef = inject<MatDialogRef<NewPacketTemplateDialogComponent, NewPacketTemplateDTO | undefined>>(MatDialogRef);
    private readonly data = inject<NewPacketTemplateDialogData>(MAT_DIALOG_DATA);
    private $ubDashboardService = inject(UBDashboardService);

    private readonly destructor$ = new Subject<void>();
    private readonly modulesSubject = new BehaviorSubject<ReadonlyArray<KeyValue>>([]);
    private readonly processesSubject = new BehaviorSubject<ReadonlyArray<KeyValue>>([]);

    @ViewChild("description") readonly descriptionInput: LgdTextComponent;
    readonly formGroup: FormGroup;
    readonly menuOptions: ReadonlyArray<KeyValue>;
    isSaving: boolean;

    constructor()
    {
        this.formGroup = new FormGroup({
            module: LgdFormControlFactory.select<KeyValue>(
                undefined,
                true,
                {
                    displayValueFn: x => x.Value
                }),
            process: LgdFormControlFactory.select<KeyValue>(
                undefined,
                true,
                {
                    displayValueFn: x => x.Value
                }),
            description: LgdFormControlFactory.text(
                undefined,
                true,
                {
                    maxLength: () => this.maxDescriptionLength
                }),
        });

        this.menuOptions = [
            {
                Key: "Today",
                Value: "{Today}",
            },
            {
                Key: "Beginning of Month",
                Value: "{BoM}",
            },
            {
                Key: "End of Month",
                Value: "{EoM}",
            },
            {
                Key: "Beginning of Quarter",
                Value: "{BoQ}",
            },
            {
                Key: "End of Quarter",
                Value: "{EoQ}",
            },
            {
                Key: "Beginning of Year",
                Value: "{BoY}",
            },
            {
                Key: "End of Year",
                Value: "{EoY}",
            },
            {
                Key: "Beginning of Week",
                Value: "{BoW}",
            },
            {
                Key: "End of Week",
                Value: "{EoW}",
            },
        ];
    }

    private get Item(): NewPacketTemplateDTO { return this.data.Item; }

    private get moduleControl(): AbstractControl<KeyValue> { return this.formGroup.get("module"); }
    private get processControl(): AbstractControl<KeyValue> { return this.formGroup.get("process"); }
    private get descriptionControl(): LgdTextFormControl { return this.formGroup?.get("description") as LgdTextFormControl; }

    get modulesObservable(): Observable<ReadonlyArray<KeyValue>> { return this.modulesSubject.asObservable(); }
    get processesObservable(): Observable<ReadonlyArray<KeyValue>> { return this.processesSubject.asObservable(); }


    get processPlaceholder(): string
    {
        return !this.moduleControl.value
            ? "First, Select Module Above"
            : `Select ${this.moduleControl.value.Value} Process`;
    }

    get canSave(): boolean { return this.formGroup.valid && !this.isSaving; }

    private get maxDescriptionLength(): number
    {
        const maxLength = 50;
        const description = this.descriptionControl?.value;
        if (!description)
        {
            return maxLength;
        }

        const tokens = /\{.*?\}/g.exec(description);
        if (!tokens)
        {
            return maxLength;
        }

        const tokenLength = tokens.reduce((sum, token) => sum + token.length, 0);

        const dateWidth = 10;
        return maxLength - (dateWidth * tokens.length) + tokenLength;
    }

    ngOnDestroy(): void
    {
        this.destructor$.next();
        this.destructor$.complete();
    }

    ngOnInit(): void
    {
        const self = this;

        this.$ubDashboardService.getModules()
            .then(result =>
            {
                this.modulesSubject.next(
                    dictionaryToList(result));

                if (this.Item)
                {
                    this.moduleControl.setValue(
                        this.modulesSubject.value.find(x => x.Key === this.Item.ModuleId));

                    this.descriptionControl.setValue(this.Item.Description);

                    return this.$ubDashboardService.getProcessTypes(this.moduleControl.value.Key)
                        .then(result =>
                        {
                            this.processesSubject.next(
                                dictionaryToList(result));

                            this.processControl.setValue(
                                this.processesSubject.value.find(x => x.Key === this.Item.ProcessId));
                        });
                }
            });

        subscribeToValueChanges(this.moduleControl, (newValue: KeyValue) =>
        {
            this.$ubDashboardService.getProcessTypes(newValue.Key)
                .then(result =>
                {
                    this.processesSubject.next(dictionaryToList(result));

                    setEnabled(this.processControl, !!newValue);
                });

        });

        subscribeToValueChanges(this.processControl, (newValue: KeyValue) =>
        {
            setEnabled(this.descriptionControl, !!newValue);
        });

        function subscribeToValueChanges<T = unknown>(control: AbstractControl<T>, subscriptionFn: (newValue: T) => void): void
        {
            control.valueChanges.pipe(takeUntil(self.destructor$))
                .subscribe(x =>
                {
                    subscriptionFn(x);
                });
        }
    }

    addToken(item: KeyValue): void
    {
        const description = this.descriptionControl.value;
        if (!description)
        {
            this.descriptionControl.setValue(item.Value);
            return;
        }

        if (this.descriptionControl.selection)
        {
            const [selectionStart, selectionEnd] = this.descriptionControl.selection;
            const front = description.substring(0, selectionStart);
            const back = description.substring(selectionEnd);
            this.descriptionControl.setValue(`${front}${item.Value}${back}`);
        }
        else
        {
            this.descriptionControl.setValue(`${description}${item.Value}`);
        }

        this.descriptionControl.focus();
    }

    cancel(): void
    {
        this.dialogRef.close();
    }

    saveTemplate(): void
    {
        this.isSaving = true;
        const item: NewPacketTemplateDTO = {
            Id: this.Item?.Id,
            ModuleId: this.moduleControl.value.Key,
            Module: this.moduleControl.value.Value,
            ProcessId: this.processControl.value.Key,
            Process: this.processControl.value.Value,
            Description: this.descriptionControl.value,
        };

        this.$ubDashboardService.saveNewPacketTemplate(item)
            .then(() =>
            {
                this.dialogRef.close(item);
            })
            .finally(() =>
            {
                this.isSaving = false;
            });
    }
}
